import { Injectable } from '@angular/core';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';

const QTR_PAYLOAD_TRACE_KEY = 'QTR_PAYLOAD_TRACE_V1';

@Injectable({
  providedIn: 'root',
})
export class CustomGlobalFilterService extends GlobalFilterService {
  private snapshot<T>(value: T): T {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return value;
    }
  }

  private shouldTraceKeys(keys: string[]): boolean {
    return keys.some((key) => ['year', 'qtr', 'quarter', 'month'].includes(String(key || '').trim().toLowerCase()));
  }

  private shouldTracePayloadConfig(config: any): boolean {
    const apiUrl = String(config?.apiUrl || '');
    const contentKeys = new Set(
      (config?.content || [])
        .map((item: any) => String(item?.key || '').trim().toLowerCase())
        .filter(Boolean),
    );

    return apiUrl.includes('/qtr-year/drop-down') ||
      apiUrl.includes('/customer-drop-down') ||
      (contentKeys.has('year') && contentKeys.has('qtr') && contentKeys.has('month'));
  }

  private trace(stage: string, details: any): void {
    console.log(`[${QTR_PAYLOAD_TRACE_KEY}] ${stage}`, this.snapshot(details));
  }

  private getScopedQtrYearFilters(config: any, filters: any[]): any[] {
    return filters;
  }

  private getPayloadOperator(attributeName: string, values: any[]): string {
    const normalizedKey = String(attributeName || '').trim().toLowerCase();
    if (['year', 'qtr', 'quarter'].includes(normalizedKey)) {
      return 'IN';
    }

    return values.length > 1 ? 'IN' : '=';
  }

  private normalizePayloadWhere(where: any[]): any[] {
    if (!Array.isArray(where) || where.length === 0) {
      return [];
    }

    const hasMonth = where.some(
      (item: any) => String(item?.attributeName || '').trim().toLowerCase() === 'month',
    );
    const timeFilterOrder = new Map([
      ['month', 0],
      ['year', 1],
      ['qtr', 2],
      ['quarter', 2],
    ]);

    const hasExplicitOr = where.some(
      (item: any) => String(item?.logicalOperator || '').trim().toUpperCase() === 'OR',
    );
    const normalized = where
      .map((item: any) => {
        const attributeValues = Array.isArray(item?.attributeValues)
          ? item.attributeValues
          : [item?.attributeValues].filter(
              (value) => value !== undefined && value !== null && value !== '',
            );

        return {
          ...item,
          attributeValues,
          operator: this.getPayloadOperator(item?.attributeName, attributeValues),
        };
      })
      .sort((a: any, b: any) => {
        if (!hasMonth || hasExplicitOr) {
          return 0;
        }

        const keyA = String(a?.attributeName || '').trim().toLowerCase();
        const keyB = String(b?.attributeName || '').trim().toLowerCase();
        return (timeFilterOrder.get(keyA) ?? 99) - (timeFilterOrder.get(keyB) ?? 99);
      });

    if (!hasExplicitOr) {
      normalized.forEach((item: any, index: number) => {
        item.logicalOperator = index < normalized.length - 1 ? 'AND' : '';
      });
    }

    return normalized;
  }

  /**
   * Overrides updateFilterSelection to handle 'values' being a string instead of an array.
   * All other methods are inherited from GlobalFilterService.
   */
  override updateFilterSelection(
    keyOrKeys: string | string[],
    values: any[] | any, // Allow non-array inputs
    isQueryParam = false,
    widgetId?: string,
    emit: boolean = true,
    filterListener?: any,
  ): void {
    const keys = Array.isArray(keyOrKeys)
      ? keyOrKeys
      : (keyOrKeys || '')
        .split(',')
        .map((k) => k.trim())
        .filter((k) => k);
    const shouldTrace = this.shouldTraceKeys(keys);

    if (shouldTrace) {
      this.trace('updateFilterSelection:input', {
        keys,
        values,
        isQueryParam,
        widgetId,
        emit,
        activeFilter: this.activeFilter,
        beforeFilters: this.getCurrentFilters(),
      });
    }

    // If month is deselected, trigger a full dashboard reset to default state.
    if (keys.includes('month') && (!values || (Array.isArray(values) && values.length === 0))) {
      if (shouldTrace) {
        this.trace('updateFilterSelection:monthCleared', { keys, values });
      }
      this.clearAllFilters();
      return;
    }

    keys.forEach((key) => {
      // Store the full value to preserve multi-select state for stable toggling/deselect.
      const valueToStore = this.cloneValue(values);

      this.selectedFilters[key] = valueToStore;

      if (isQueryParam) {
        this.filters.queryParams[key] = this.cloneValue(values);
      } else {
        this.filters.requestParams[key] = this.cloneValue(values);
      }
    });

    this.widgetId = widgetId || null;
    if (emit) {
      const filterData = {
        ...this.filters,
        widgetId: this.widgetId,
        changedFilterKey: keys.length === 1 ? keys[0] : keys,
        filterListener,
      };

      if (shouldTrace) {
        this.trace('updateFilterSelection:emit', filterData);
      }

      this.filterSubject.next(filterData);
      this.currentSelectedFilter.next({ key: keys });
    }
  }
  override buildPayload(config: any, filters: any[]): any {
    const whereToSend = this.normalizePayloadWhere(this.getScopedQtrYearFilters(
      config,
      filters.length > 0 ? [...filters] : [],
    ));

    // Send user-selected filters as-is.
    // If nothing is selected, send where: [] — same default as customer_name dropdown.
    // This lets the backend return all available options for year, qtr, month and customer_name.
    const payload = {
      filter: {
        where: whereToSend,
        join: {},
        orderBy: {},
      },
    };

    if (this.shouldTracePayloadConfig(config)) {
      this.trace('buildPayload:qtrYearDropdown', {
        activeFilter: this.activeFilter,
        inputFilters: filters,
        scopedFilters: whereToSend,
        payload,
      });
    }

    console.log('[CustomGlobalFilterService] buildPayload:', payload);
    return payload;
  }
}
